import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Banner from '@/components/Banner';
import CookieNotice from '@/components/CookieNotice';
import { NavBar } from '@/components/NavBar';
import { useDownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { openDownloadInNewWindow, safeDownload } from '@/utils/downloadHelper';
import { getRecommendedDownloadType } from '@/utils/systemDetector';
import { showNoSuitableVersionToast } from '@/utils/toastHelper';
import { isMobileByUA } from '@/utils/ua';
import { sendDownloadClickEvent, sendWeblogEvent } from '@/utils/weblogger';

import {
  AgentSection,
  CTASection,
  ContextSection,
  DeliverySection,
  HeroSection,
} from './components';
import ControlSphere from './components/Sections/ControlSphere';
import styles from './index.module.less';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { downloadInfo } = useDownloadInfo();
  const [recommendedType, setRecommendedType] = useState<string | null>(null);

  // 初始化时检测用户系统
  useEffect(() => {
    const recommendedDownloadType = getRecommendedDownloadType();
    setRecommendedType(recommendedDownloadType);
  }, []);

  // 处理 All Download 按钮点击 - 跳转到下载页面
  const handleAllDownload = useCallback(() => {
    // 埋点上报：All Download按钮点击
    sendDownloadClickEvent('all_platforms', 'redirect', 'home');

    navigate('/download');
  }, [navigate]);

  // 处理 Download 按钮点击 - 直接下载
  const handleDirectDownload = useCallback(async () => {
    if (isMobileByUA() && downloadInfo?.arm) {
      safeDownload(downloadInfo.arm);
      return;
    }

    if (!downloadInfo || !recommendedType) {
      // 如果没有下载信息或无法检测系统，跳转到下载页面
      sendDownloadClickEvent(recommendedType || 'unknown', 'redirect_no_info', 'home');

      // 显示无合适版本的提示
      showNoSuitableVersionToast();
      navigate('/download');
      return;
    }

    let targetDownloadInfo = null;
    let platformName = '';

    // 根据推荐类型获取下载信息
    switch (recommendedType) {
      case 'apple':
        targetDownloadInfo = downloadInfo.arm;
        platformName = 'apple';
        break;
      case 'intel':
        targetDownloadInfo = downloadInfo.intel;
        platformName = 'intel';
        break;
      case 'windows':
        targetDownloadInfo = downloadInfo.windows;
        platformName = 'windows';
        break;
      default:
        // 不支持的系统，跳转到下载页面
        sendDownloadClickEvent('unsupported', 'redirect_unsupported', 'home');

        // 显示无合适版本的提示
        showNoSuitableVersionToast();
        navigate('/download');
        return;
    }

    if (targetDownloadInfo) {
      try {
        // 尝试安全下载
        const downloadSuccess = await safeDownload(targetDownloadInfo);

        if (downloadSuccess) {
          // 下载成功时的埋点上报
          sendDownloadClickEvent(platformName, targetDownloadInfo.version || 'unknown', 'home');
        } else {
          // 如果下载失败，尝试在新窗口打开
          const downloadUrl = targetDownloadInfo.dmgUrl || targetDownloadInfo.url;
          if (downloadUrl) {
            // 新窗口下载的埋点上报
            sendDownloadClickEvent(platformName, targetDownloadInfo.version || 'unknown', 'home');

            openDownloadInNewWindow(downloadUrl);
          } else {
            // 最后的备用方案：跳转到下载页面
            sendDownloadClickEvent(platformName, 'redirect_no_url', 'home');

            navigate('/download');
          }
        }
      } catch (error) {
        console.error('下载失败:', error);
        // 出错时跳转到下载页面
        sendDownloadClickEvent(platformName, 'redirect_error', 'home');

        navigate('/download');
      }
    } else {
      // 没有下载信息，跳转到下载页面
      sendDownloadClickEvent(platformName, 'redirect_no_target', 'home');

      // 显示无合适版本的提示
      showNoSuitableVersionToast();
      navigate('/download');
    }
  }, [downloadInfo, recommendedType, navigate]);

  return (
    <div className={styles.container}>
      <Banner />

      <NavBar />

      {/* New Hero Section based on Figma design */}
      <HeroSection />

      {/* Agent Section based on Figma design */}
      <AgentSection />

      {/* Delivery Section based on Figma design */}
      <DeliverySection />

      {/* Context Section based on Figma design */}
      <ContextSection />

      {/* Control Section based on Figma design */}
      <ControlSphere />

      {/* CTA Section based on Figma design */}
      <CTASection
        onDownloadClick={handleDirectDownload}
        onAllDownloadClick={handleAllDownload}
        recommendedType={recommendedType}
      />

      {/* Cookie Notice - only show on home page */}
      <CookieNotice onlyShowOnHome={true} />
    </div>
  );
};

export default Home;
